import type { Page } from "../../api";
import { TabPage } from "../../PageFrame";
import { Legend, Swatch } from "../bars";
import { VBars, type BarPoint } from "../charts";
import type { PageProps } from "../index";
import { dateGB, monthGB, PageHead } from "../kit";
import { ALL_ROWS } from "../params";

export interface SpeedRow {
  /** First day of the month, or the Monday of the week (ISO date). */
  period: string;
  options_added: number;
}

export type Grain = "month" | "week";

const DAY = 86_400_000;

/** ISO week number and the year it belongs to, from the week's Monday. */
export function isoWeek(monday: string): { week: number; year: number } {
  const thursday = new Date(new Date(monday).getTime() + 3 * DAY);
  const jan4 = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((thursday.getTime() - jan4.getTime()) / DAY - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return { week, year: thursday.getUTCFullYear() };
}

/** "W13 '25" on the axis; "24 Mar 2025 to 30 Mar 2025 (Week 13)" in the tooltip. */
export function weekPoint(row: SpeedRow): BarPoint {
  const { week, year } = isoWeek(row.period);
  const sunday = new Date(new Date(row.period).getTime() + 6 * DAY).toISOString();
  return {
    label: `W${week} '${String(year).slice(2)}`,
    hint: `${dateGB(row.period)} to ${dateGB(sunday)} (Week ${week})`,
    value: row.options_added,
  };
}

export function speedPoints(rows: SpeedRow[], grain: Grain): BarPoint[] {
  return grain === "week" ? rows.map(weekPoint) : rows.map((row) => ({ label: monthGB(row.period), value: row.options_added }));
}

export const speedTotal = (rows: SpeedRow[]) => rows.reduce((sum, row) => sum + row.options_added, 0);

export const SPEED_SUB: Record<Grain, string> = {
  month: "Sourcing options added per month",
  week: "Sourcing options added per week · weeks with none show 0",
};

export function SpeedBody({ rows, grain }: { rows: SpeedRow[]; grain: Grain }) {
  return (
    <>
      <Legend>
        <Swatch className="bg-ks-purple">Options added</Swatch>
      </Legend>
      <VBars
        title={grain === "week" ? "Options added per week" : "Options added per month"}
        slot={grain === "week" ? 22 : 44}
        rotate={grain === "week"}
        every={grain === "week" ? 2 : 1}
        points={speedPoints(rows, grain)}
        toEnd
      />
    </>
  );
}

function SpeedPage({ tab, areaName, grain }: PageProps & { grain: Grain }) {
  return (
    <TabPage<Page<SpeedRow>>
      tab={tab}
      areaName={areaName}
      // Speed counts every option that was added, including the ones dropped since.
      extra={{ dropped: "show", limit: ALL_ROWS }}
      isEmpty={(data) => data.items.length === 0}
      head={(data) => (
        <PageHead
          title={tab.label}
          sub={data ? SPEED_SUB[grain] : undefined}
          kpi={data ? { label: "Total", value: speedTotal(data.items).toLocaleString("en-GB") } : null}
        />
      )}
    >
      {(data) => <SpeedBody rows={data.items} grain={grain} />}
    </TabPage>
  );
}

export function SpeedMonthPage(props: PageProps) {
  return <SpeedPage {...props} grain="month" />;
}

export function SpeedWeekPage(props: PageProps) {
  return <SpeedPage {...props} grain="week" />;
}
